import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from "@/components/ui/checkbox"; 
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from "@/lib/utils"; 
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle, Check, ChevronsUpDown } from 'lucide-react';
import { useFinanceiroDropdownData, CATEGORIA_PAGAMENTO } from '@/services/FinanceiroService'; // Importa hooks e constantes

// Estado inicial
const initialStatePagamento = {
  data_emissao: new Date().toISOString().split('T')[0],
  descricao: '', 
  categoria: '',
  fornecedor_id: '', 
  valor_base: '',
  forma_pagamento: 'PIX',
  num_parcelas: 1,
  conta_id: '',
  ja_foi_pago: false,
  recorrente: false,
  recorrencia_tipo: 'MENSAL',
  recorrencia_quantidade: 12,
  recorrencia_infinita: false,
};

// Filtro do Combobox
const comboboxFilter = (value, search) => {
  return value.toLowerCase().includes(search.toLowerCase());
};

export default function NovoPagamentoView() {
  const navigate = useNavigate();
  const [pagamentoForm, setPagamentoForm] = useState(initialStatePagamento);
  const [fornecedorPopoverOpen, setFornecedorPopoverOpen] = useState(false);
  
  // Carrega dados dos dropdowns
  const { contasList, beneficiariosPagar, loadingDropdowns } = useFinanceiroDropdownData();

  // Define conta padrão quando a lista carregar
  useEffect(() => {
    if (contasList.length > 0) {
      setPagamentoForm(prev => ({...prev, conta_id: contasList[0].id }));
    }
  }, [contasList]);
  
  const handlePagamentoFormChange = (field, value) => {
    setPagamentoForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmitPagamento = async (e) => {
    e.preventDefault();
    
    const data = {
      data_emissao: pagamentoForm.data_emissao,
      descricao: pagamentoForm.descricao,
      categoria: pagamentoForm.categoria,
      fornecedor_id: (pagamentoForm.fornecedor_id === 'avulso' || pagamentoForm.fornecedor_id === '') ? null : pagamentoForm.fornecedor_id,
      valor_base: parseFloat(pagamentoForm.valor_base),
      forma_pagamento: pagamentoForm.forma_pagamento,
      num_parcelas: parseInt(pagamentoForm.num_parcelas) || 1,
      conta_id: (pagamentoForm.conta_id === 'nenhuma' || pagamentoForm.conta_id === '') ? null : pagamentoForm.conta_id,
      ja_foi_pago: pagamentoForm.ja_foi_pago,
      recorrente: pagamentoForm.recorrente,
      recorrencia_tipo: pagamentoForm.recorrente ? pagamentoForm.recorrencia_tipo : null,
      recorrencia_quantidade: parseInt(pagamentoForm.recorrencia_quantidade) || 1,
      recorrencia_infinita: pagamentoForm.recorrencia_infinita,
    };
    
    if (!data.valor_base || data.valor_base <= 0) {
      toast.error("O 'Valor Base' deve ser maior que zero.");
      return;
    }
    if (!data.descricao || !data.categoria) {
      toast.error("Preencha 'Descrição' e 'Categoria'.");
      return;
    }
    if (data.recorrente && !data.recorrencia_tipo) {
       toast.error("Selecione o tipo de recorrência.");
       return;
    }
    
    try {
      await axiosInstance.post('/despesas', data); 
      toast.success("Pagamento(s) criado(s) com sucesso!");
      // Redireciona de volta para a lista
      navigate('/financeiro/contas-a-pagar');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar pagamento.");
      console.error(error);
    }
  };

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Novo Pagamento
        </h1>
        <p className="text-gray-600">Crie um novo lançamento de saída (avulso).</p>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 max-w-4xl mx-auto">
        <form onSubmit={handleSubmitPagamento} className="space-y-6">
          
          {/* Informações Gerais */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-4">Informações Gerais</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              <div className="md:col-span-2">
                <Label>Descrição</Label>
                <Input 
                  value={pagamentoForm.descricao} 
                  onChange={(e) => handlePagamentoFormChange('descricao', e.target.value)} 
                  placeholder="Ex: Aluguel do escritório" 
                  required 
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={pagamentoForm.categoria} onValueChange={(v) => handlePagamentoFormChange('categoria', v)} required>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIA_PAGAMENTO.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Data 1º Vencimento</Label>
                <Input type="date" value={pagamentoForm.data_emissao} onChange={(e) => handlePagamentoFormChange('data_emissao', e.target.value)} required />
              </div>
              <div className="md:col-span-2">
                <Label>Pagar a (Fornecedor / Vendedor)</Label>
                <Popover open={fornecedorPopoverOpen} onOpenChange={setFornecedorPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={fornecedorPopoverOpen}
                      className="w-full justify-between font-normal"
                      disabled={loadingDropdowns}
                    >
                      {pagamentoForm.fornecedor_id
                        ? beneficiariosPagar.find((b) => b.id === pagamentoForm.fornecedor_id)?.nome
                        : "Selecione um beneficiário..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                    <Command filter={comboboxFilter}> 
                      <CommandInput placeholder="Buscar beneficiário..." />
                      <CommandEmpty>Nenhum beneficiário encontrado.</CommandEmpty>
                      <CommandGroup className="overflow-y-auto">
                        <CommandItem
                          key="avulso"
                          value="Nenhum (Avulso)" 
                          onSelect={() => {
                            handlePagamentoFormChange('fornecedor_id', 'avulso');
                            setFornecedorPopoverOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", pagamentoForm.fornecedor_id === "avulso" ? "opacity-100" : "opacity-0")} />
                          Nenhum (Avulso)
                        </CommandItem>
                        {beneficiariosPagar.map((beneficiario) => (
                          <CommandItem
                            key={beneficiario.id}
                            value={beneficiario.nome} 
                            onSelect={(currentValue) => {
                              const selectedId = beneficiariosPagar.find(b => b.nome.toLowerCase() === currentValue.toLowerCase())?.id;
                              handlePagamentoFormChange('fornecedor_id', selectedId || 'avulso');
                              setFornecedorPopoverOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", pagamentoForm.fornecedor_id === beneficiario.id ? "opacity-100" : "opacity-0")} />
                            {beneficiario.nome}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          {/* Pagamento */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-4">Pagamento</h4>
             <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Valor Base (R$)</Label>
                  <Input type="number" step="0.01" value={pagamentoForm.valor_base} onChange={(e) => handlePagamentoFormChange('valor_base', e.target.value)} placeholder="100.00" required />
                  <p className="text-xs text-gray-500 mt-1">Valor de cada parcela/recorrência.</p>
                </div>
                 <div>
                  <Label>Forma de Pagamento</Label>
                  <Select value={pagamentoForm.forma_pagamento} onValueChange={(v) => handlePagamentoFormChange('forma_pagamento', v)} required>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PIX">Pix</SelectItem>
                      <SelectItem value="CARTAO_DEBITO">Cartão de Débito</SelectItem>
                      <SelectItem value="CARTAO_CREDITO">Cartão de Crédito</SelectItem>
                      <SelectItem value="DEPOSITO">Depósito</SelectItem>
                      <SelectItem value="BOLETO">Boleto</SelectItem>
                      <SelectItem value="OUTRO">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conta de Origem</Label>
                  <Select value={pagamentoForm.conta_id} onValueChange={(v) => handlePagamentoFormChange('conta_id', v)} disabled={loadingDropdowns}>
                    <SelectTrigger><SelectValue placeholder="Opcional..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhuma">Nenhuma</SelectItem>
                      {contasList.map(c => <SelectItem key={c.id} value={c.id}>{c.nome} ({c.nome_banco})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="ja_foi_pago_despesa"
                      checked={pagamentoForm.ja_foi_pago}
                      onCheckedChange={(c) => handlePagamentoFormChange('ja_foi_pago', c)}
                    />
                    <Label htmlFor="ja_foi_pago_despesa" className="cursor-pointer">Já foi pago?</Label>
                  </div>
                </div>
             </div>
          </div>
          
          {/* Parcelas vs Recorrência */}
          <div className="grid grid-cols-2 gap-6">
            <div className={`p-4 border rounded-md ${pagamentoForm.recorrente ? 'opacity-50 bg-gray-50' : ''}`}>
              <h4 className="font-medium mb-4">Parcelamento (Total Fixo)</h4>
              <div>
                <Label>Nº de Parcelas</Label>
                <Input 
                  type="number" 
                  value={pagamentoForm.num_parcelas} 
                  onChange={(e) => handlePagamentoFormChange('num_parcelas', e.target.value)} 
                  disabled={pagamentoForm.recorrente}
                  min="1"
                />
                 <p className="text-xs text-gray-500 mt-1">Ex: Valor Base 100,00, 2 Parcelas = 2x 100,00.</p>
                 <p className="text-xs text-gray-500 mt-1">(Vencimentos mensais)</p>
              </div>
            </div>
            <div className="p-4 border rounded-md space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="recorrente_despesa"
                  checked={pagamentoForm.recorrente}
                  onCheckedChange={(c) => {
                    handlePagamentoFormChange('recorrente', c);
                    if (c) { handlePagamentoFormChange('num_parcelas', 1); } 
                  }}
                />
                <Label htmlFor="recorrente_despesa" className="font-medium cursor-pointer">Recorrente (Assinatura)</Label>
              </div>
              <div className={`space-y-4 ${!pagamentoForm.recorrente ? 'opacity-50' : ''}`}>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                    <Label>Tipo de Recorrência</Label>
                    <Select 
                      value={pagamentoForm.recorrencia_tipo} 
                      onValueChange={(v) => handlePagamentoFormChange('recorrencia_tipo', v)}
                      disabled={!pagamentoForm.recorrente}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SEMANAL">Semanal</SelectItem>
                        <SelectItem value="QUINZENAL">Quinzenal</SelectItem>
                        <SelectItem value="MENSAL">Mensal</SelectItem>
                        <SelectItem value="SEMESTRAL">Semestral</SelectItem>
                        <SelectItem value="ANUAL">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                   </div>
                   <div>
                    <Label>Qtde. Pagamentos</Label>
                    <Input 
                      type="number" 
                      value={pagamentoForm.recorrencia_quantidade} 
                      onChange={(e) => handlePagamentoFormChange('recorrencia_quantidade', e.target.value)} 
                      disabled={!pagamentoForm.recorrente || pagamentoForm.recorrencia_infinita}
                      min="1"
                    />
                   </div>
                 </div>
                 <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="infinito_despesa"
                    checked={pagamentoForm.recorrencia_infinita}
                    onCheckedChange={(c) => handlePagamentoFormChange('recorrencia_infinita', c)}
                    disabled={!pagamentoForm.recorrente}
                  />
                  <Label htmlFor="infinito_despesa" className="cursor-pointer">Infinito</Label>
                 </div>
                 <p className="text-xs text-gray-500 mt-1">Ex: Valor Base 100,00, Recorrente Mensal = 100,00 todo mês.</p>
              </div>
            </div>
          </div>
          
          <Button type="submit" className="w-full text-lg py-6" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CheckCircle className="w-5 h-5 mr-2" />
            Criar Lançamento(s)
          </Button>
          
        </form>
      </div>
    </div>
  );
}